// ============================================================================
// QUELLE ENTITÉ CRÉE-T-ON QUAND ON ENRICHIT UNE FACETTE D'ARTICLE ?
//
// POURQUOI CE MODULE. Trois écrans offrent « créer à la volée » sous un sélecteur de référentiel —
// la fiche article, la gamme, le nouvel article — et chacun portait SA réponse. Elles ont divergé :
// la fiche envoyait `reference_list` + `kind: 'season'` pour une saison, les deux autres envoyaient
// l'entité `season`, qui N'EXISTE PAS au registre. La porte humaine la refuse (422 `unknown_entity`),
// donc le bouton échouait — un défaut qui ne se voit qu'au clic d'un commerçant, sur un écran par
// ailleurs parfait (§23.0, « option offerte non implémentée »).
//
// ⚠️ LA CORRESPONDANCE EST DONC ÉCRITE UNE FOIS, ET ELLE EST FERMÉE : `Record<ArticleFacet, …>`
// oblige le compilateur à réclamer une entrée quand une facette apparaît. Une table ouverte aurait
// laissé la neuvième facette sans cible, en silence — exactement le trou qu'on vient de boucher.
//
// ⚠️ LA SAISON EST LE CAS QUI JUSTIFIE LE `kind`. Elle n'a pas de table à elle : elle vit dans la
// liste générique, dont l'identité est COMPOSITE (kind + code). Sans le `kind`, « HIVER » en saison
// et « HIVER » en civilité seraient la même ligne.
// ============================================================================
import { api, type ArticleFacet } from '../api';

/** L'entité master-data derrière une facette, et la moitié d'identité qu'elle réclame. */
export interface FacetTarget {
  /** Le nom d'entité que la porte humaine attend — il DOIT exister au registre du serveur. */
  readonly entity: string;
  /** Première moitié de l'identité, pour les seules valeurs logées dans la liste générique. */
  readonly kind?: string;
}

/**
 * ⚠️ FERMÉE PAR LE TYPE. Ajouter une facette à `ArticleFacet` sans lui donner sa cible ici ne
 * compile pas — c'est la seule façon d'empêcher le défaut de revenir par une neuvième dimension.
 */
export const FACET_TARGETS: Readonly<Record<ArticleFacet, FacetTarget>> = {
  fam: { entity: 'family' },
  sfam: { entity: 'sub_family' },
  sect: { entity: 'sector' },
  sup: { entity: 'supplier' },
  marq: { entity: 'brand' },
  coll: { entity: 'collection' },
  coul: { entity: 'color' },
  sais: { entity: 'reference_list', kind: 'season' },
};

/**
 * Crée une valeur de référentiel et rend celle qui a été RETENUE.
 *
 * ⚠️ CRÉER SEULEMENT, JAMAIS « CRÉER OU METTRE À JOUR » : l'identité est le code NORMALISÉ, donc recréer
 *    « VESTES » quand « Vestes » existe désignait la même ligne — et l'écriture ordinaire la RÉÉCRIVAIT en
 *    silence (son orthographe changeait dans toutes les caisses). Le serveur refuse désormais, en nommant la
 *    ligne existante ; la garde du sélecteur, qui interroge le serveur, propose de la choisir avant d'en arriver là.
 * ⚠️ ON REND LA VALEUR TELLE QU'ON L'A ENVOYÉE, pas une invention : le producteur garde le libellé tapé.
 *
 * ⚠️ LE CHAMP D'IDENTITÉ N'EST PAS LE MÊME PARTOUT, et le corps envoyé EST l'entrée du producteur :
 * familles, secteurs, marques et fournisseurs s'identifient par `code` (normalisé en `code_norm`),
 * la GRILLE DE TAILLES par `name`. Envoyer `code` à une grille produirait une entrée sans identité —
 * refusée au mieux, silencieuse au pire. Le défaut reste `code`, majoritaire.
 */
export async function createReferential(
  entity: string,
  value: string,
  kind?: string,
  field: 'code' | 'name' = 'code',
): Promise<string> {
  const v = value.trim();
  const body: Record<string, string> = kind ? { kind, [field]: v } : { [field]: v };
  await api.createMasterDataOnly(entity, body, `ref:${entity}:${kind ?? ''}`);
  return v;
}

/** Crée la valeur d'une FACETTE — la cible vient de la table, jamais de l'appelant. */
export async function createFacetValue(facet: ArticleFacet, value: string): Promise<string> {
  const target = FACET_TARGETS[facet];
  return createReferential(target.entity, value, target.kind);
}
